<template>
  <div class="be-ap-card" :class="{ 'be-ap-card--sold-out': isSoldOut, 'be-ap-card--vertical': imageFirst || heroOverlay, 'be-ap-card--hero': heroOverlay, 'be-ap-card--stacked': stackedAction, 'be-ap-card--no-image': hideImage }">
    <div v-if="!hideImage" class="be-ap-card__media" @click="$emit('detail')">
      <img :src="image" :alt="name" class="be-ap-card__img" />
      <span v-if="categoryLabel && !heroOverlay" class="be-ap-card__badge be-ap-card__badge--left">
        {{ categoryLabel }}
      </span>
      <span v-if="variantCount > 0" class="be-ap-card__badge be-ap-card__badge--right">
        {{ variantCount }} {{ $pluralWord(variantCount, 'Variant') }}
      </span>

      <template v-if="heroOverlay">
        <span v-if="durationLabel" class="be-ap-card__duration-pill">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          {{ durationLabel }}
        </span>
        <div class="be-ap-card__overlay">
          <p class="be-ap-card__overlay-name">{{ name }}</p>
        </div>
      </template>
    </div>

    <div class="be-ap-card__body">
      <div v-if="!heroOverlay || hideImage" class="be-ap-card__head" @click="$emit('detail')">
        <p class="be-ap-card__name">{{ name }}</p>
        <div v-if="description" class="be-ap-card__desc html-desc" v-html="description"></div>
      </div>

      <!-- Badges normally live on the image; without it they need a place in the body. -->
      <div v-if="hasMetaChips" class="be-ap-card__meta">
        <span v-if="categoryLabel" class="be-ap-card__meta-chip">{{ categoryLabel }}</span>
        <span v-if="durationLabel" class="be-ap-card__meta-chip">{{ durationLabel }}</span>
        <span v-if="variantCount > 0" class="be-ap-card__meta-chip">
          {{ variantCount }} {{ $pluralWord(variantCount, 'Variant') }}
        </span>
      </div>

      <template v-if="heroOverlay && !hideImage">
        <p v-if="description" class="be-ap-card__hero-desc">{{ description }}</p>
      </template>

      <div class="be-ap-card__footer" :class="{ 'be-ap-card__footer--divided': heroOverlay && description && !hideImage }">
        <div class="be-ap-card__price-wrap">
          <p v-if="pricePrefix" class="be-ap-card__price-prefix">{{ pricePrefix }}</p>
          <template v-if="heroOverlay">
            <p v-if="discountLabel || oldPrice" class="be-ap-card__price-line be-ap-card__price-line--old">
              <span v-if="discountLabel" class="be-ap-card__discount-chip">{{ discountLabel }}</span>
              <span v-if="oldPrice" class="be-ap-card__price-old">{{ oldPrice }}</span>
            </p>
            <p class="be-ap-card__price-line">
              <span class="be-ap-card__price">{{ price }}</span>
              <span v-if="unit" class="be-ap-card__unit">/ {{ unit }}</span>
              <span v-if="taxNote" class="be-ap-card__tax">{{ taxNote }}</span>
            </p>
          </template>
          <p v-else class="be-ap-card__price-line">
            <span v-if="oldPrice" class="be-ap-card__price-old">{{ oldPrice }}</span>
            <span class="be-ap-card__price">{{ price }}</span>
            <span v-if="unit" class="be-ap-card__unit">/ {{ unit }}</span>
            <span v-if="taxNote" class="be-ap-card__tax">{{ taxNote }}</span>
          </p>
        </div>
        <div class="be-ap-card__action">
          <slot name="action" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BeAdditionalProductCard',
  props: {
    image: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    price: {
      type: String,
      default: '',
    },
    oldPrice: {
      type: String,
      default: '',
    },
    pricePrefix: {
      type: String,
      default: '',
    },
    unit: {
      type: String,
      default: '',
    },
    categoryLabel: {
      type: String,
      default: '',
    },
    durationLabel: {
      type: String,
      default: '',
    },
    taxNote: {
      type: String,
      default: '',
    },
    discountLabel: {
      type: String,
      default: '',
    },
    variantCount: {
      type: Number,
      default: 0,
    },
    isSoldOut: {
      type: Boolean,
      default: false,
    },
    imageFirst: {
      type: Boolean,
      default: false,
    },
    heroOverlay: {
      type: Boolean,
      default: false,
    },
    stackedAction: {
      type: Boolean,
      default: false,
    },
    hideImage: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasMetaChips() {
      if (!this.hideImage) return false;
      return !!(this.categoryLabel || this.durationLabel || this.variantCount > 0);
    },
  },
};
</script>

<style scoped>
.be-ap-card {
  display: flex;
  width: 100%;
  height: 100%;
  background: #fff;
  border: 1px solid var(--be-v3-border, #d5d9e0);
  border-radius: var(--be-v3-radius, 10px);
  overflow: hidden;
  transition: transform var(--be-v3-dur-base, 220ms) var(--be-v3-ease, ease-out),
    box-shadow var(--be-v3-dur-base, 220ms) var(--be-v3-ease, ease-out),
    border-color var(--be-v3-dur-base, 220ms) var(--be-v3-ease, ease-out);
}
.be-ap-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.09);
}
.be-ap-card__media {
  position: relative;
  flex-shrink: 0;
  width: 132px;
  cursor: pointer;
  overflow: hidden;
}
.be-ap-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--be-v3-dur-slow, 300ms) var(--be-v3-ease, ease-out);
}
.be-ap-card:hover .be-ap-card__img {
  transform: scale(1.04);
}
.be-ap-card__badge {
  position: absolute;
  bottom: 8px;
  max-width: calc(100% - 16px);
  padding: 2px 8px;
  background: rgba(255, 255, 255, 0.92);
  color: #1a1a2e;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.be-ap-card__badge--left {
  left: 8px;
}
.be-ap-card__badge--right {
  right: 8px;
}
.be-ap-card--sold-out .be-ap-card__img {
  filter: grayscale(0.6);
  opacity: 0.75;
}
.be-ap-card__body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
}
.be-ap-card__head {
  cursor: pointer;
}
.be-ap-card__name {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.be-ap-card__desc {
  margin: 0;
  font-size: 13px;
  color: #888;
  line-height: 1.4;
  max-height: 2.8em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.be-ap-card__footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.be-ap-card__price-wrap {
  min-width: 0;
}
.be-ap-card__price-prefix {
  margin: 0;
  font-size: 11px;
  color: #999;
}
.be-ap-card__price-line {
  margin: 0;
  white-space: nowrap;
}
.be-ap-card__price-old {
  font-size: 12px;
  color: #bbb;
  text-decoration: line-through;
  margin-right: 4px;
}
.be-ap-card__price {
  font-size: 16px;
  font-weight: 700;
  color: var(--merchant-primary-color, #79a33a);
}
.be-ap-card__unit {
  font-size: 12px;
  color: #888;
  margin-left: 2px;
}
.be-ap-card__action {
  flex-shrink: 0;
}
/* Horizontal (block) cards stay compact — they hug their content but keep a
   small floor so a no-description item's thumbnail stays well-proportioned.
   Row-level equal height comes from the grid column stretch, not a fixed value. */
.be-ap-card:not(.be-ap-card--vertical):not(.be-ap-card--hero) {
  min-height: 96px;
}
/* Center the body so low-info cards look balanced and any stretch-slack (when a
   row sibling is taller) distributes evenly instead of leaving a bottom void. */
.be-ap-card:not(.be-ap-card--vertical):not(.be-ap-card--hero) .be-ap-card__body {
  justify-content: center;
}
@media screen and (max-width: 400px) {
  .be-ap-card:not(.be-ap-card--vertical) .be-ap-card__media {
    width: 108px;
  }
}

/* Image-first (vertical) variant — used by activity/spa tiles. */
.be-ap-card--vertical {
  flex-direction: column;
}
.be-ap-card--vertical .be-ap-card__media {
  width: 100%;
  height: 170px;
}
.be-ap-card--vertical .be-ap-card__body {
  padding: 14px;
}
.be-ap-card--vertical .be-ap-card__name {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* Stacked-footer variant — used by restaurant grid tiles: price on its own line
   with a full-width action (button spans the card) instead of the price/action row. */
.be-ap-card--stacked .be-ap-card__footer {
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}
.be-ap-card--stacked .be-ap-card__action {
  width: 100%;
}

/* Hero-overlay variant — used by activity/spa tiles: name + badge sit on the image. */
.be-ap-card--hero .be-ap-card__media {
  height: 200px;
}
.be-ap-card--hero .be-ap-card__overlay {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 32px 14px 12px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0.15) 60%, rgba(0, 0, 0, 0) 100%);
  pointer-events: none;
}
.be-ap-card--hero .be-ap-card__overlay-name {
  margin: 0;
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
/* Duration pill — same translucent-white pill + absolute-corner + shadow
   technique the old .be-ap-card__discount badge used, now in that slot. */
.be-ap-card__duration-pill {
  position: absolute;
  top: 10px;
  left: 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 9px 4px 7px;
  background: rgba(255, 255, 255, 0.92);
  color: #1a1a2e;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  white-space: nowrap;
}
.be-ap-card__duration-pill svg {
  flex-shrink: 0;
  color: var(--merchant-primary-color, #79a33a);
}
.be-ap-card--hero .be-ap-card__body {
  justify-content: flex-start;
  gap: 10px;
  padding: 13px 14px 14px;
}
.be-ap-card__hero-desc {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: #7a7a85;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.be-ap-card--hero .be-ap-card__footer {
  align-items: center;
  margin-top: auto;
}
.be-ap-card--hero .be-ap-card__footer--divided {
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}
.be-ap-card--hero .be-ap-card__price {
  font-size: 18px;
}
.be-ap-card__tax {
  margin-left: 4px;
  font-size: 10.5px;
  color: #aaa;
}

/* Discount chip — same solid merchant-color pill token the discount badge
   used on the image, now grouped inline with the price. */
.be-ap-card__discount-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  background: var(--merchant-button-color, var(--merchant-primary-color, #79a33a));
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: 999px;
}

/* Hero price is split into two short, stacked lines (old price + discount
   chip, then the main price) instead of one long row — keeps the column
   narrow so it never stretches wider than the button side. */
.be-ap-card--hero .be-ap-card__price-line {
  display: flex;
  align-items: center;
  gap: 5px;
}
.be-ap-card--hero .be-ap-card__price-line--old {
  margin-bottom: 2px;
}
.be-ap-card--hero .be-ap-card__price-old {
  margin-right: 0;
}

/* Give the button a touch more presence so the now-shorter price column
   and the action column read as balanced. */
.be-ap-card--hero .be-ap-card__action ::v-deep .be-ap-add-btn {
  padding: 10px 20px;
  font-size: 13.5px;
}
/* Image-less mode (merchant config hide_product_image): the media box isn't
   rendered at all, so every variant collapses to the same text-only column. */
.be-ap-card--no-image {
  flex-direction: column;
}
.be-ap-card--no-image .be-ap-card__name {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.be-ap-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0 0;
}
.be-ap-card--hero .be-ap-card__meta {
  margin-top: 0;
}
.be-ap-card__meta-chip {
  padding: 2px 8px;
  background: #f4f5f7;
  color: #1a1a2e;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  white-space: nowrap;
}

/* Jost (the v3 default content font) reads heavier at 600/700 than BeV3Sans did at
   the same weight, so these labels drop to Regular when no custom font is set. */
.be-v3-content-font-default .be-ap-card__badge,
.be-v3-content-font-default .be-ap-card__meta-chip,
.be-v3-content-font-default .be-ap-card__name,
.be-v3-content-font-default .be-ap-card--hero .be-ap-card__overlay-name,
.be-v3-content-font-default .be-ap-card__duration-pill,
.be-v3-content-font-default .be-ap-card__discount-chip {
  font-weight: 400;
}
/* Price keeps a touch more emphasis than the other labels — same light-stroke
   nudge used for the room rate's final price. */
.be-v3-content-font-default .be-ap-card__price {
  font-weight: 500;
  -webkit-text-stroke: 0.15px currentColor;
}
</style>

<!-- Unscoped: action controls shared by each module's #action slot content. -->
<style>
/* The description is injected via v-html, so its children don't carry the
   scoped attribute. Keep rich markup (images, block margins) from stretching
   the compact card past its two-line clamp. */
.be-ap-card__desc img,
.be-ap-card__desc svg,
.be-ap-card__desc figure,
.be-ap-card__desc iframe {
  display: none;
}
.be-ap-card__desc p,
.be-ap-card__desc ul,
.be-ap-card__desc ol,
.be-ap-card__desc div {
  display: inline;
  margin: 0;
  padding: 0;
}
.be-ap-add-btn {
  border: none;
  background: var(--merchant-button-color, var(--merchant-primary-color, #79a33a));
  color: #fff;
  border-radius: var(--be-v3-button-radius, 8px);
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}
.be-ap-btn-outline {
  border: 1px solid var(--merchant-primary-color, #79a33a);
  background: #fff;
  color: var(--merchant-primary-color, #79a33a);
  border-radius: var(--be-v3-button-radius, 8px);
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}
.be-ap-btn-disabled {
  border: none;
  background: #e9e9e9;
  color: #999;
  border-radius: var(--be-v3-button-radius, 8px);
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: not-allowed;
}
.be-ap-qty {
  display: flex;
  align-items: center;
  gap: 6px;
}
/* In stacked tiles the action fills the card width, so center the qty stepper. */
.be-ap-card--stacked .be-ap-qty {
  justify-content: center;
}
.be-ap-qty__btn {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: var(--be-v3-button-radius, 8px);
  background: var(--merchant-primary-color, #79a33a);
  color: #fff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  user-select: none;
}
.be-ap-qty__num {
  min-width: 18px;
  text-align: center;
  font-weight: 700;
  font-size: 14px;
}
/* Jost (the v3 default content font) reads heavier at 600/700 than BeV3Sans did at
   the same weight, so these labels drop to Regular when no custom font is set. */
.be-v3-content-font-default .be-ap-add-btn,
.be-v3-content-font-default .be-ap-btn-outline,
.be-v3-content-font-default .be-ap-btn-disabled,
.be-v3-content-font-default .be-ap-qty__num {
  font-weight: 400;
}
.be-ap-note-btn {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: var(--be-v3-button-radius, 8px);
  border: 1px solid #e0e0e0;
  background: #fafafa;
  cursor: pointer;
}

/* The additional-service detail/variant modals reuse the legacy templates and are
   portaled to <body>, outside .be-landing — so the v3 font/radius vars don't inherit
   and the legacy Nunito/Lato utilities take over. Each v3 product passes this class
   through b-modal's content-class to re-point font + radius to the v3 tokens (custom
   fonts fall back to BeV3Sans, matching the other v3 modals). Colors already resolve
   to --merchant-* vars globally, so they need no override here. */
.be-v3-modal {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif;
  border-radius: var(--be-v3-radius, 10px);
}
/* Re-point the hardcoded typography utilities (Nunito/Lato, some with !important). The
   :not() list holds off Bootstrap's own .modal-body / .card-body which [class*='body-']
   would otherwise catch. Icon-glyph classes are untouched so iconsmind stays intact. */
.be-v3-modal [class*='body-']:not(.modal-body):not(.card-body):not(.popover-body):not(.toast-body),
.be-v3-modal .font-regular,
.be-v3-modal .font-lato,
.be-v3-modal .font-lato-regular,
.be-v3-modal .sub-heading-1-2,
.be-v3-modal .overline {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif !important;
}
.be-v3-modal .font-extrabold {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif !important;
  font-weight: 700;
}
.be-v3-modal .font-bold {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif !important;
  font-weight: 600;
}
.be-v3-modal .font-semibold {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif !important;
  font-weight: 400;
}
/* Unify the legacy full-corner card/image radii to the v3 card token. One-sided radii
   (radius-l-6, radius-12-t, …) and .rounded-circle close buttons are left as-is. */
.be-v3-modal .radius-6,
.be-v3-modal .radius-12,
.be-v3-modal .rounded-6 {
  border-radius: var(--be-v3-radius, 10px);
}
/* Buttons and steppers follow the v3 button token. */
.be-v3-modal .g-btn,
.be-v3-modal .ga-btn-primary-lg,
.be-v3-modal .pos-order-qty,
.be-v3-modal .pos-order-qty-sm,
.be-v3-modal .spa-select {
  border-radius: var(--be-v3-button-radius, 8px);
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeAdditionalProductCard.vue